import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, ShieldCheck, Lock, Save, RotateCcw } from "lucide-react";

const ACTION_LABELS = { view: "View", create: "Create", edit: "Edit", delete: "Delete" };

function FragmentGroup({ groupName, groupModules, actions, effectivePerms, activeKey, toggleAction, toggleAllModule }) {
  const disabled = activeKey === "managing_director";
  return (
    <>
      <tr className="bg-zinc-50/50">
        <td colSpan={actions.length + 2} className="px-4 py-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{groupName}</td>
      </tr>
      {groupModules.map((m) => {
        const rowActions = effectivePerms[m.key] || [];
        const hasAll = actions.every((a) => rowActions.includes(a));
        return (
          <tr key={m.key} className="border-t border-zinc-100" data-testid={`perm-row-${m.key}`}>
            <td className="px-4 py-2.5">
              <div className="font-medium">{m.label}</div>
              <div className="text-[10px] font-mono text-zinc-400">{m.key}</div>
            </td>
            {actions.map((a) => (
              <td key={a} className="px-3 py-2.5 text-center">
                <input
                  type="checkbox"
                  checked={rowActions.includes(a)}
                  disabled={disabled}
                  onChange={() => toggleAction(m.key, a)}
                  data-testid={`perm-${m.key}-${a}`}
                  className="w-4 h-4 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </td>
            ))}
            <td className="px-3 py-2.5 text-center">
              <input
                type="checkbox"
                checked={hasAll}
                disabled={disabled}
                onChange={(e) => toggleAllModule(m.key, e.target.checked)}
                data-testid={`perm-${m.key}-all`}
                className="w-4 h-4 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
              />
            </td>
          </tr>
        );
      })}
    </>
  );
}

export default function RolesPermissions() {
  const qc = useQueryClient();
  const [selectedRole, setSelectedRole] = useState(null);
  const [pending, setPending] = useState({}); // { role_key: { module: [actions] } }
  const [newRoleOpen, setNewRoleOpen] = useState(false);
  const [newRole, setNewRole] = useState({ key: "", name: "", description: "" });
  const [pendingDelete, setPendingDelete] = useState(null);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await api.get("/roles")).data,
  });
  const { data: modulesInfo = { modules: [], actions: [] } } = useQuery({
    queryKey: ["role-modules"],
    queryFn: async () => (await api.get("/roles/modules")).data,
  });
  const modules = modulesInfo.modules || [];
  const actions = modulesInfo.actions || ["view", "create", "edit", "delete"];

  const modulesByGroup = useMemo(() => {
    const g = {};
    for (const m of modules) { (g[m.group] = g[m.group] || []).push(m); }
    return g;
  }, [modules]);

  const active = roles.find((r) => r.key === selectedRole) || roles[0];
  const activeKey = active?.key;

  // Merge server perms with local pending edits
  const effectivePerms = useMemo(() => {
    if (!active) return {};
    return pending[active.key] || active.permissions || {};
  }, [active, pending]);

  const isDirty = useMemo(() => {
    if (!active) return false;
    return JSON.stringify(pending[active.key] || null) !== "null";
  }, [pending, active]);

  const savePerms = useMutation({
    mutationFn: () => api.put(`/roles/${activeKey}/permissions`, { permissions: effectivePerms }),
    onSuccess: () => {
      toast.success("Permissions saved");
      qc.invalidateQueries({ queryKey: ["roles"] });
      qc.invalidateQueries({ queryKey: ["nav"] });
      setPending((p) => { const q = { ...p }; delete q[activeKey]; return q; });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/roles", body),
    onSuccess: (res) => {
      toast.success(`Role '${res.data.name}' created`);
      qc.invalidateQueries({ queryKey: ["roles"] });
      setNewRoleOpen(false); setNewRole({ key: "", name: "", description: "" });
      setSelectedRole(res.data.key);
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const delMut = useMutation({
    mutationFn: (key) => api.delete(`/roles/${key}`),
    onSuccess: () => {
      toast.success("Role deleted");
      qc.invalidateQueries({ queryKey: ["roles"] });
      setPendingDelete(null);
      if (pendingDelete && selectedRole === pendingDelete.key) setSelectedRole(null);
    },
    onError: (e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setPendingDelete(null); },
  });

  const toggleAction = (moduleKey, action) => {
    if (!active || active.key === "managing_director") {
      toast.error("Managing Director permissions cannot be modified.");
      return;
    }
    const current = { ...(pending[active.key] || active.permissions || {}) };
    const existing = current[moduleKey] || [];
    const has = existing.includes(action);
    let next;
    if (has) {
      next = existing.filter((a) => a !== action);
      if (next.length === 0) {
        // Removing view when it's the only action = remove the whole module
        const { [moduleKey]: _, ...rest } = current;
        setPending({ ...pending, [active.key]: rest });
        return;
      }
    } else {
      next = [...existing, action];
    }
    setPending({ ...pending, [active.key]: { ...current, [moduleKey]: next } });
  };

  const toggleAllModule = (moduleKey, wantOn) => {
    if (!active || active.key === "managing_director") return;
    const current = { ...(pending[active.key] || active.permissions || {}) };
    if (wantOn) {
      current[moduleKey] = [...actions];
    } else {
      delete current[moduleKey];
    }
    setPending({ ...pending, [active.key]: current });
  };

  return (
    <div data-testid="roles-page">
      <PageHeader
        eyebrow="Administration · Security"
        title="Roles & Permissions"
        description="Configure who can see and do what across the ERP. Changes take effect instantly — no code deploy needed."
        actions={
          <Dialog open={newRoleOpen} onOpenChange={setNewRoleOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-role-btn"><Plus className="w-4 h-4 mr-1.5" /> New Role</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Custom Role</DialogTitle>
                <DialogDescription>Define a new security role. You'll set its permissions after creating it.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Role Key (lowercase, no spaces)</Label>
                  <Input placeholder="e.g. bim_lead" value={newRole.key} onChange={(e) => setNewRole({ ...newRole, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") })} data-testid="new-role-key" />
                </div>
                <div>
                  <Label>Display Name</Label>
                  <Input placeholder="e.g. BIM Lead" value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} data-testid="new-role-name" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea placeholder="What this role owns…" value={newRole.description} onChange={(e) => setNewRole({ ...newRole, description: e.target.value })} data-testid="new-role-description" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setNewRoleOpen(false)}>Cancel</Button>
                <Button onClick={() => createMut.mutate(newRole)} disabled={!newRole.key || !newRole.name || createMut.isPending} data-testid="new-role-submit">
                  {createMut.isPending ? "Creating…" : "Create Role"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="max-w-[1400px] mx-auto px-8 py-6 flex gap-6">
        {/* Left: role list */}
        <aside className="w-64 shrink-0 space-y-1" data-testid="roles-list">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 px-2">Roles ({roles.length})</div>
          {isLoading && <div className="text-sm text-zinc-500 px-2">Loading…</div>}
          {roles.map((r) => (
            <button key={r.key} onClick={() => setSelectedRole(r.key)}
              className={`w-full text-left px-3 py-2 rounded-md border ${activeKey === r.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 hover:bg-zinc-50 border-zinc-200"}`}
              data-testid={`role-${r.key}`}
            >
              <div className="flex items-center gap-1.5">
                {r.is_system && <Lock className="w-3 h-3 opacity-60" />}
                <span className="font-medium text-sm truncate">{r.name}</span>
              </div>
              <div className={`text-[10px] mt-0.5 ${activeKey === r.key ? "text-zinc-300" : "text-zinc-500"}`}>
                {Object.keys(r.permissions || {}).length} modules · {r.user_count} user{r.user_count === 1 ? "" : "s"}
              </div>
            </button>
          ))}
        </aside>

        {/* Right: matrix editor */}
        <section className="flex-1 min-w-0" data-testid="matrix-editor">
          {!active ? (
            <div className="text-sm text-zinc-500">Select a role on the left.</div>
          ) : (
            <div className="space-y-4">
              <div className="border border-zinc-200 rounded-lg bg-white p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-zinc-700" />
                      <h2 className="text-lg font-semibold">{active.name}</h2>
                      {active.is_system && <Badge variant="outline" className="text-[9px]">SYSTEM</Badge>}
                      {active.key === "managing_director" && <Badge className="text-[9px] bg-amber-100 text-amber-900 border-amber-200">Full-access · Locked</Badge>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{active.description}</div>
                    <div className="text-[11px] font-mono text-zinc-500 mt-1">role_key: {active.key} · {active.user_count} assigned user{active.user_count === 1 ? "" : "s"}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isDirty && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => setPending((p) => { const q = { ...p }; delete q[activeKey]; return q; })} data-testid="perms-reset">
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
                        </Button>
                        <Button size="sm" onClick={() => savePerms.mutate()} disabled={savePerms.isPending} data-testid="perms-save">
                          <Save className="w-3.5 h-3.5 mr-1.5" /> {savePerms.isPending ? "Saving…" : "Save changes"}
                        </Button>
                      </>
                    )}
                    {!active.is_system && (
                      <Button variant="ghost" size="sm" onClick={() => setPendingDelete(active)} className="text-red-600 hover:text-red-700" data-testid="role-delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="border border-zinc-200 rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="perms-matrix">
                  <thead className="bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left px-4 py-3">Module</th>
                      {actions.map((a) => (
                        <th key={a} className="px-3 py-3 text-center">{ACTION_LABELS[a]}</th>
                      ))}
                      <th className="px-3 py-3 text-center">All</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(modulesByGroup).map(([groupName, groupModules]) => (
                      <FragmentGroup key={`group-${groupName}`} groupName={groupName} groupModules={groupModules} actions={actions} effectivePerms={effectivePerms} activeKey={active.key} toggleAction={toggleAction} toggleAllModule={toggleAllModule} />
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-zinc-500 px-1">
                {isDirty ? "Unsaved changes. Click Save to apply." : "Everything saved. Changes take effect on the next page load for each user."}
              </div>
            </div>
          )}
        </section>
      </div>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent data-testid="role-del-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role {pendingDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the role and its permission matrix. If any user is still assigned, the delete will be blocked.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-del-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => pendingDelete && delMut.mutate(pendingDelete.key)} data-testid="role-del-confirm">
              {delMut.isPending ? "Deleting…" : "Delete role"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
